import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";

// The Bug-Hunter Glass Ledger — Community front, Founder-approved 2026-07-02+.
// Public transparency for the reward program. Anonymous rollup only —
// no reporter names, no report bodies, no reveal. Dignity outranks visibility.

export default function BugHunterWall() {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    fetch(`${backend}/api/bug-hunter/wall`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setStats(d))
      .catch(() => setErr(true));
  }, [backend]);

  return (
    <Layout>
      <section
        data-testid="bug-hunter-wall"
        className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 border-b border-white/[0.05]"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] opacity-[0.09] blur-3xl"
            style={{
              background: "radial-gradient(circle at 50% 0%,#ff5a00,transparent 60%)",
            }}
          />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 sm:px-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00]">
            The Bug-Hunter Glass Ledger · Public Record
          </p>
          <h1
            className="font-display uppercase leading-[0.92] mt-6 text-[#ededed]"
            style={{ fontSize: "clamp(2.5rem, 7vw, 5rem)" }}
          >
            Find a bug. <span className="text-[#ff5a00]">Earn $5.</span>
            <br />
            Stack it. Forever.
          </h1>
          <p className="font-display italic text-lg sm:text-2xl text-[#a1a1aa] mt-8 max-w-3xl leading-relaxed">
            Every confirmed bug that ships a fix earns the hunter $5 on their
            email. One running coupon per hunter · applied automatically at
            your next Resilience checkout. No cap. No expiry.
          </p>
        </div>
      </section>

      <section className="relative py-20 sm:py-28 border-b border-white/[0.05]">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10">
          {err && (
            <p
              data-testid="bug-hunter-wall-error"
              className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00]"
            >
              Ledger offline this cycle · retry shortly
            </p>
          )}

          {stats && (
            <div className="grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-4">
              <StatCard
                label="Reports on file"
                value={stats.reports_total}
                testid="wall-reports-total"
              />
              <StatCard
                label="Confirmed bugs"
                value={stats.reports_confirmed}
                testid="wall-reports-confirmed"
              />
              <StatCard
                label="Fixes shipped"
                value={stats.reports_fixed}
                testid="wall-reports-fixed"
                accent
              />
              <StatCard
                label="Hunters paid"
                value={stats.hunter_count}
                testid="wall-hunter-count"
              />
              <StatCard
                label="Coupon paid out"
                value={`$${(stats.total_paid_out_usd || 0).toFixed(0)}`}
                testid="wall-paid-out"
              />
              <StatCard
                label="Coupon redeemed"
                value={`$${(stats.total_redeemed_usd || 0).toFixed(0)}`}
                testid="wall-redeemed"
              />
              <StatCard
                label="Per confirmed fix"
                value={`$${(stats.reward_usd_per_confirmed_fix || 5).toFixed(0)}`}
                testid="wall-per-fix"
              />
              <StatCard
                label="Standing rule"
                value="Stack · No cap"
                testid="wall-standing-rule"
                small
              />
            </div>
          )}

          {!stats && !err && (
            <p
              data-testid="bug-hunter-wall-loading"
              className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#71717a]"
            >
              Loading the ledger…
            </p>
          )}
        </div>
      </section>

      <section className="relative py-20 sm:py-28">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-6">
            The rules of the wall
          </p>
          <ul className="font-body text-base sm:text-lg text-[#d4d4d8] space-y-4 border-l border-[#ff5a00]/40 pl-6">
            <li>
              One coupon per email — every confirmed + shipped fix stacks
              another $5 onto the same coupon.
            </li>
            <li>
              Auto-applied at checkout — you don&apos;t enter a code, we don&apos;t
              email you one. The balance is deducted from your next order.
            </li>
            <li>
              Anonymous public wall — the counts above are real; the names,
              emails, and report bodies stay in-house. Dignity outranks
              visibility.
            </li>
            <li>
              Family-wide policy — Sage and Aethelgard mirror this ruling on
              their own storefronts.
            </li>
            <li>
              Click the <span className="text-[#ff5a00]">Bug Hunter</span>{" "}
              ribbon in the bottom-right corner of any page to file your
              first report.
            </li>
          </ul>
        </div>
      </section>
    </Layout>
  );
}

function StatCard({ label, value, testid, accent = false, small = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      data-testid={testid}
      className={`relative border p-5 sm:p-6 bg-[#0b0b0d] ${
        accent ? "border-[#ff5a00]/50" : "border-white/10"
      }`}
    >
      <p className="font-mono text-[9px] uppercase tracking-[0.32em] text-[#a1a1aa] mb-3">
        {label}
      </p>
      <p
        className={`font-display ${accent ? "text-[#ff5a00]" : "text-[#ededed]"} leading-none`}
        style={{
          fontSize: small
            ? "clamp(0.9rem, 1.6vw, 1.15rem)"
            : "clamp(1.6rem, 3.4vw, 2.4rem)",
        }}
      >
        {value}
      </p>
    </motion.div>
  );
}
